import { DatePipe } from "@angular/common";
import { Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef } from "@angular/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { asyncScheduler, debounceTime, firstValueFrom, skip, Subscription, tap } from "rxjs";
import { AppState, RoleMgr, ShowRequest } from "../../core";
import {
  ScoutPointInfo,
  ScoutTaskService,
  UserService
} from "../../core/services";
import {
  TableColumn,
  TableHeaderTemplateContext,
  TableViewComponentBase,
  TableViewQueryRequest
} from "../../shared/components/table-view/table-view-component-base";
import { VolunteerTaskPointViewRecord } from "../models/volunteer-task-point-record";
import { VolunteerTaskPointViewService}  from "../services/volunteer-task-point-view.service";
import { VolunteerTaskService } from "../services/volunteer-task.service";
import { VolunteerService } from "../services/volunteer.service";
import { activity_status } from "../../core/models";
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";
import { TableRecActionOkResult, TableRecActionResult, TableRecsParallelAction } from "../../shared/components/table-view/table-view-action";
import { VolunteerStatusChooserModal } from "../components/volunteer-status-chooser.modal";
import { VolunteerTaskViewService } from "../services/volunteer-task-view.service";
import { TestParamsModalEditResult, VolunteerTaskPointTestParamsModalComponent } from "../volunteer-task-point/volunteer-task-point-test-params-modal/volunteer-task-point-test-params-modal.component";
import { ScoutTaskDoc } from "../../core/schema/scout-task-schema";

export type TaskPtRecs = {
  recs: VolunteerTaskPointViewRecord[];
  scoutInfoByRecId: {
    [recId: number]: ScoutPointInfo;
  };
  mobilePhotoIdsByRecId: {
    [recId: number]: number;
  };
};

export type VolunteerPtRecInfo = {
  rec: VolunteerTaskPointViewRecord;
  scoutInfo: ScoutPointInfo;
};

@Component({
  selector: "app-volunteer-task-points-table",
  templateUrl: "../../shared/components/table-view/table-view.component.html",
  styleUrls: [
    "../../shared/components/table-view/scss/table-view.scss",
    "../../shared/components/table-view/scss/app-tab-full-height.scss",
    "../../shared/components/table-view/scss/fixed-headers-and-column.scss",
  ],
})
export class VolunteerTaskPointsTableComponent
  extends TableViewComponentBase<VolunteerTaskPointViewRecord>
  implements OnInit, OnDestroy
{
  @Input() stage: string;
  @Input() taskId: number;
  @Input("editColumnHeaderTmplRef") set editColumnHeaderTmplRefSetter(
    ref: TemplateRef<TableHeaderTemplateContext>
  ) {
    const column = this.tableColumns.find((c) => c.template == "edit-point");
    if (column) {
      column.headerTemplateRef = ref;
    }
  }

  filterKeyUpSubscription: Subscription;
  editColumn: TableColumn;

  constructor(
    appState: AppState,
    modalService: BsModalService,
    public volunteerService: VolunteerService,
    public volunteerTaskService: VolunteerTaskService,
    public volunteerTaskViewService: VolunteerTaskViewService,
    public volunteerTaskPointViewService: VolunteerTaskPointViewService,
    elementRef: ElementRef,
    datePipe: DatePipe,
    public roleMgr: RoleMgr,
    public userService: UserService,
    public scoutService: ScoutTaskService
  ) {
    super(appState, modalService, elementRef, datePipe);

    const idColumn = {
      title: "ID",
      name: "id",
      template: "number",
      visible: false,
    };
    this.editColumn = { title: "", name: null, visible: true, template: "edit-point" };
    this.tableColumns = [
      idColumn,
      { title: "Task ID", name: "taskId", visible: false },
      { title: "Testbed Point ID", name: "pointId", visible: true },
      { title: "Building", name: "buildingId", visible: false },
      { title: "Building Name", name: "buildingName", visible: false },
      { title: "Testbed Path ID", name: "pathId", visible: false },
      {
        title: "Scout Point",
        name: null,
        visible: true,
        template: "scout-point",
        headerCellCssClass: 'scout-point-table-header'
      },
      { title: "Volunteer ID", name: "volunteerId", visible: false },
      { title: "Volunteer", name: "volunteerName", visible: false },
      { title: "Volunteer Email", name: "volunteerEmail", visible: false },
      {
        title: "Latitude",
        name: "latitude",
        visible: false,
        template: "number",
      },
      {
        title: "Longitude",
        name: "longitude",
        visible: false,
        template: "number",
      },
      { title: "GEOID", name: "geoid", visible: false },
      {
        title: "Altitude GEOID Meters",
        name: "altitudeGeoidMeters",
        visible: false,
        template: "number",
      },
      { title: "WGS84", name: "wgs84", visible: false },
      {
        title: "Altitude Ellipsoid Meters",
        name: "altitudeEllipsoidMeters",
        visible: false,
        template: "number",
      },
      //{ title: "Civic Address", name: "civicAddress", visible: true },
      { title: "Floor Num", name: "floorNum", visible: false },
      { title: "Floor", name: "floorName", visible: false },
      { title: "Floor/Description", name: "floorAndDescript", visible: false },
      {
        title: "Location Description",
        name: "locationDescription",
        visible: false,
      },
      { title: "Window Count", name: "windowCnt", visible: false },
      {
        title: "Floor To Ceiling (ft in)",
        name: "floorToCeilingFtIn",
        visible: false,
      },
      { title: "Clutter", name: "clutter", visible: false },
      { title: "HVAC", name: "hvac", visible: false },
      { title: "Deleted", name: "deleted", template: 'badboolean', visible: false},
      { title: "Modified By ID", name: "modifiedBy", visible: false },
      {
        title: "Modified (local)",
        name: "modifiedAtText",
        template: "time",
        visible: false,
      },
      { title: "Revision ID", name: "revisionId", visible: false },
      { title: "QR", name: "pointIdQr", visible: false },
      { title: "Calls", name: "callsPlacedCnt", visible: true },
      { title: "Last Call (local)", name: "lastCallTime", template: 'localtime', visible: true },
      { title: "Last Call Duration", name: "lastCallDuration", visible: false },
      { title: "Last Valid RUT ID", name: "lastValidRutId", visible: false },

      { title: "QR Validation", name: "qrValidationRequired", template: 'boolean', visible: false },
      { title: "Photo Validation", name: "photoValidationRequired", template: 'boolean', visible: false },
      { title: "Location Fix Validation", name: "computeFixValidationRequired", template: 'boolean', visible: false },
      { title: "Call Interval (s)", name: "callIntervalSecs", template: 'number', visible: false },
      { title: "Target Total Calls", name: "targetCallCnt", template: 'number', visible: false },
      { title: "Target Call Duration (s)", name: "targetCallDurationSecs", template: 'number', visible: false },
      { title: "Dial#", name: "dialNumber", visible: false },

      { title: "Status", name: "statusName", visible: true },
      this.editColumn,
    ];

    this.idColumns = [idColumn];
    //this.bStickyBulkActions = true;
    this.bShowBulkActionsColumn = true;
    this.bShowHeader = false;
    this.bShowFilterRow = false;
    const rowActions = [];

    if ( this.roleMgr.canEditTasks ) {
      rowActions.push(
        new TableRecsParallelAction<VolunteerTaskPointViewRecord,{chosenStatus:number}>(
          'Change Status',
          ({rec,action:{context}})=>this.changeTaskPtStatusAction(rec,context.chosenStatus),
          {chosenStatus: undefined}
        ).setMaxConcurrent(3).setOnBegin(({recs,action:{context}})=>this.selectTaskPtStatusAction(recs,context))),
      rowActions.push(
        new TableRecsParallelAction<VolunteerTaskPointViewRecord,TestParamsModalEditResult>(
          'Edit Test Parameters',
          ({rec,action:{context}})=>this.saveTestCallParametersAction(rec,context),
          {testParameters: undefined, changes: []}
        ).setMaxConcurrent(3).setOnBegin(({recs,action:{context}})=>this.selectTestCallParametersAction(recs,context)))
      }
    this.setRowActions(rowActions);
  }

  selectTaskPtStatusAction = async (recs: VolunteerTaskPointViewRecord[], context: {chosenStatus:number}) => {
    if ( !recs[0] ) {
      const msg = 'Invalid state. No records selected.';
      console.warn(msg, recs);
      throw new Error(msg);
    }
    try {
      const chosenStatus = await VolunteerStatusChooserModal.chooseStatus(
        recs[0],
        this.modalService,
        `Point(s): ${recs?.map(r=>r.pointId)?.join(', ')}`
      );
      if (chosenStatus !== undefined) {
        console.log("chosenStatus", chosenStatus);
        context.chosenStatus = chosenStatus;
        return true;
      } else {
        console.log("no chosen status (cancelled)", chosenStatus);
        return false;
      }
    } catch (e) {
      console.error(e);
      e.message = "Failed assigning status. " + e.message;
      throw e;
    }
  };

  changeTaskPtStatusAction = async (rec: VolunteerTaskPointViewRecord, chosenStatus: number) => {
    try {
      const patchedRec = await this.volunteerTaskPointViewService.patchTaskPoint({id: rec.id,status: chosenStatus});
      this.updateRows([patchedRec]);
      return new TableRecActionOkResult(rec);
    } catch ( e: any ) {
      console.error(e);
      return new TableRecActionResult(rec,false,`Error updating status from ${activity_status.Names[rec.status]} to ${chosenStatus}. ${e?.message}`);
    }
  }


  selectTestCallParametersAction = async (testPts: VolunteerTaskPointViewRecord[], context: TestParamsModalEditResult) => {
    if ( !testPts[0] ) {
      const msg = 'Invalid state. No records selected.';
      console.warn(msg, testPts);
      throw new Error(msg);
    }
    try {
      const taskConfig = await this.volunteerTaskService.getRemoteConfig();
      const { testParameters, changes, cancelled } = await VolunteerTaskPointTestParamsModalComponent.selectTestParams(
        this.modalService,
        taskConfig.testPoint,
        testPts[0]
        );
      if ( !cancelled ) {
        context.testParameters = testParameters;
        context.changes = changes;
        return true;
      } else {
        console.log("no test param changes so cancelling save action", testParameters);
        return false;
      }
    } catch (e) {
      console.error(e);
      e.message = 'Issue selected test parameters. ' + e.message;
      throw(e)
    }
  };

  saveTestCallParametersAction = async (rec: VolunteerTaskPointViewRecord, editResult: TestParamsModalEditResult) => {
    try {
      const updatedRec = await this.volunteerTaskPointViewService.updateCustomTestParams(rec.id,editResult.testParameters);
      this.updateRows([updatedRec]);
      return new TableRecActionOkResult(rec);
    } catch ( e: any ) {
      console.error(e);
      return new TableRecActionResult(rec,false,`Error updating test parameters. ${e?.message}`);
    }
  }


  initKeyUpSubscription = () => {
    this.filterKeyUpSubscription?.unsubscribe();
    this.filterKeyUpSubscription = this.filterKeyUp$
      .pipe(
        tap((e) => e.event.key == "Enter" && this.initKeyUpSubscription()), // reset if enter force load already
        debounceTime(700)
      )
      .subscribe(() => {this.load().catch(e=>this.volunteerTaskPointViewService.publishError(e,this))});
  };

  scoutSub: Subscription;
  scoutInfoByRecId: { [recId: string]: ScoutPointInfo } = {};
  mobilePhotoIdsByRecId:  { [recId: string]: number } = {};
  savingTaskPointId: number = null;

  init() {
    // table was initially designed to just show single task so got to change defaults if showing all tasks
    if ( !this.taskId ) {
      this.bShowHeader = true;
      this.bShowFilterRow = true;
      const addlVisible = ['deleted','taskId', 'pathId', 'volunteerName', ];
      this.tableColumns.filter(tc=>addlVisible.includes(tc.name)).forEach(tc=>tc.visible=true);
      this.editColumn.visible = false;
    }
    super.init();
  }

  ngOnInit(): void {
    this.initKeyUpSubscription();
    this.init();
    //this.scoutSub = this.records$.subscribe(async (recs) => {
    //  await this.initScoutInfoByRecId(recs);
    //});
  }

  ngOnDestroy(): void {
    this.filterKeyUpSubscription?.unsubscribe();
    this.scoutSub?.unsubscribe();
    this.scoutPathsSubscriptions.forEach(s=>s.unsubscribe());
    this.destroy();
  }

  scoutPathsSubscriptions: Subscription[] = [];

  async initScoutInfoByRecId( recs: VolunteerTaskPointViewRecord[]) {
    try {
      this.scoutPathsSubscriptions.forEach(s=>s.unsubscribe());
      this.scoutPathsSubscriptions = [];
      this.scoutInfoByRecId = {};
      this.scoutInfoByRecId =
        await this.volunteerTaskPointViewService.buildScoutInfoForTaskPoints(
          recs
        );
      const uniqueScouts: ScoutTaskDoc[] = [];
      Object.values(this.scoutInfoByRecId).forEach(si=>{
        if ( !uniqueScouts.includes(si.scout)) {
          uniqueScouts.push(si.scout);
          this.scoutPathsSubscriptions.push(
            si.scout.get$('scoutPaths').pipe(skip(1)).subscribe( scoutPaths => {
            asyncScheduler.schedule(()=>{
              this.load().catch(e=>this.volunteerTaskPointViewService.publishError(e,this));
            });
          }));
        }
      })
    } catch (error: any) {
      console.error(error);
      this.volunteerTaskPointViewService.publishError(error,this);
    }
  }

  showBuilding = (record: VolunteerTaskPointViewRecord) => {
    this.showTabFn(new ShowRequest("tab", "building", record.buildingId, null));
  };

  showVolunteerPoint = (args: { taskId: number, taskPointId: number}) => {
    this.showTabFn(
      new ShowRequest(
        "tab",
        "volunteer task",
        JSON.stringify(args)
      )
    );
  };

  doQuery = async (queryFilter: TableViewQueryRequest) => {
    // modifiedat is displayed in UI as local time but it is UTC in DB so convert the user filter to UTC
    const modifiedAtFilter = queryFilter.filters?.find(
      (f) => f.key == "modifiedAt"
    );
    if (modifiedAtFilter) {
      console.log(
        `doQuery(${JSON.stringify(
          queryFilter
        )}) has modifedat (converting local to UTC)`
      );
    }
    if ( this.taskId) {
      if ( !queryFilter.filters ) {
        queryFilter.filters = []
      }
      queryFilter.filters = queryFilter.filters.filter(f=>f.key!='taskId');
      queryFilter.filters.push( { key: 'taskId', value: `${this.taskId}`, mode: '='});
      queryFilter.filters.push( { key: 'status', value: `${activity_status.Id.ARCHIVED}`, mode: '!='});
    }
    if ( !queryFilter.sorts?.length ) {
      queryFilter.sorts = [
        { key: 'pointId', order: 'ASC'}
      ]
    }
    const vTaskPointsResult = await this.volunteerTaskPointViewService.find(queryFilter);
    await this.initScoutInfoByRecId(vTaskPointsResult.rows);
    return vTaskPointsResult;
  };

  onTaskPointStatusEdited = async (rec: VolunteerTaskPointViewRecord, event: Event) => {
    const htmlSelectElement = event.target as HTMLSelectElement;
    //this.isLoading = true;
    this.savingTaskPointId = rec?.id;
    try {
      const status = parseInt(htmlSelectElement.value);
      htmlSelectElement.value = `${rec.status}`; // set drop down to old value until db save is successful

      let doSave = true;
      if ( status === activity_status.Id.ARCHIVED ) {
        doSave = false;
        const initialState = {
          onYes: () => doSave = true,
          title: `Archive Task Point?`,
          question: `<div class="pt-2">Archive task point and remove from visible points?</div>
              <div class="m-2 small font-italic text-danger"><i class='text-warning fa fa-exclamation-triangle mr-1'></i>
              Cannot be reversed from this application.</div>`,
          focusOnNo: true,
          yesStyle: 'btn btn-danger'
        };
        const modalRef = this.modalService.show(ConfirmModalComponent, {
            ignoreBackdropClick: true,
            id: 20230707,
            initialState
        });
        await firstValueFrom(modalRef.onHidden);
      }

      if ( doSave ) {
        const taskPoint = await this.volunteerTaskPointViewService.patchTaskPoint({
          id: rec.id,
          revisionId: rec.revisionId,
          status
        });
        this.updateRows([taskPoint]);
        console.log(taskPoint);
        this.appState.notifySuccess(`Status changed to ${taskPoint.statusName}`, this, 2);
      }
    } catch (error) {
      console.error(error);
      this.errorMsg =
        this.volunteerService.extractErrorMsg(error);
    } finally {
      //this.isLoading = false;
      this.savingTaskPointId = null;
    }
  };

  getName = () => "volunteer-points-table";
}
